import * as React from 'react';
import { Stack, SxProps, Theme, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { TooltipButton } from '../miscellaneous-components/tooltip-button';
import { type AnalyticsEmissionTypeToggle } from '../../../shared/utils/default-market-based-utils';

const BASIS_EXPLANATION =
  'Location-based uses the average grid emissions where you used electricity. ' +
  'Market-based reflects the tariffs and certificates you actually bought.';

/**
 * The Location-Based / Market-Based scope 2 basis toggle, shared by the analytics and reduction
 * surfaces so the explanation renders the same everywhere.
 */
export const EmissionBasisToggle: React.FC<{
  value: AnalyticsEmissionTypeToggle;
  onChange: (next: AnalyticsEmissionTypeToggle) => void;
  orientation?: 'horizontal' | 'vertical';
  /** Stretch the button group to fill the row (mobile layouts). */
  fullWidth?: boolean;
  /** Per-button sx so each surface keeps its existing button sizing. */
  buttonSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}> = ({ value, onChange, orientation = 'horizontal', fullWidth = false, buttonSx, sx }) => {
  const renderOption = (option: AnalyticsEmissionTypeToggle, label: string) => (
    <ToggleButton value={option} sx={buttonSx}>
      <Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>{label}</Typography>
    </ToggleButton>
  );

  return (
    <Stack
      direction='row'
      alignItems={orientation === 'vertical' ? 'flex-start' : 'center'}
      spacing={0.5}
      sx={sx}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_e, next: AnalyticsEmissionTypeToggle | null) => { if (next !== null) onChange(next); }}
        orientation={orientation}
        aria-label='scope 2 reporting basis'
        sx={{ width: fullWidth ? '100%' : 'auto' }}
      >
        {renderOption('locationBased', 'Location-Based')}
        {renderOption('marketBased', 'Market-Based')}
      </ToggleButtonGroup>
      <TooltipButton
        title='Location-based vs market-based'
        text={BASIS_EXPLANATION}
        placement='bottom-end'
        noPadding
      />
    </Stack>
  );
};
